import classNames from "classnames";
import { useId } from "react";

const Switch = ({
  checked = false,
  onChange,
  id,
  size = "medium",
  label,
  labelPosition = "right",
  disabled = false,
}: {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  id?: string;
  size?: "small" | "medium" | "large";
  label?: string;
  labelPosition?: "left" | "right";
  disabled?: boolean;
}) => {
  const generatedId = useId();
  const uniqueId = (id || generatedId) + " SC-ToggleShowHide";

  // Size configuration
  const sizeConfig = {
    small: {
      container: "w-9 h-5",
      toggle: "after:h-4 after:w-4",
      position: "after:top-0.5 after:start-0.5",
    },
    medium: {
      container: "w-11 h-6",
      toggle: "after:h-5 after:w-5",
      position: "after:top-0.5 after:start-0.5",
    },
    large: {
      container: "w-14 h-7",
      toggle: "after:h-6 after:w-6",
      position: "after:top-[-1.5px] after:start-0",
    },
  };

  const config = sizeConfig[size];

  return (
    <label className="inline-flex items-end cursor-pointer" htmlFor={uniqueId}>
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        onChange={(e) => onChange?.(e.target.checked)}
        id={uniqueId}
        disabled={disabled}
      />
      {label && labelPosition === "left" && (
        <span className="me-3 text-sm font-normal text-data">{label}</span>
      )}
      <div
        className={classNames(
          "relative bg-toggle rounded-full peer peer-focus:outline-none",
          "peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full",
          "peer-checked:after:border-white peer-checked:bg-success-icon",
          "after:content-[''] after:absolute after:bg-white after:border-gray-300",
          "after:border after:rounded-full after:transition-all after:shadow-[0_0_1px]",
          `${disabled && "opacity-50 cursor-not-allowed"}`,
          config.container,
          `${config.position}`,
          `${config.toggle}`,
        )}
      />
      {label && labelPosition === "right" && (
        <span className="ms-3 text-sm font-medium text-gray-900">{label}</span>
      )}
    </label>
  );
};

export default Switch;
